export const getSoldRefillStatusMessage = (data) =>
  String(data?.sold_refill?.message ?? '').trim();

export const getUnmatchedReplaceStatusMessage = (data) =>
  String(data?.unmatched_replace?.message ?? '').trim();

export const hasAiSuggestionItems = (data) => {
  const soldCount = data?.sold_refill?.items?.length || 0;
  const unmatchedCount = data?.unmatched_replace?.items?.length || 0;
  return soldCount > 0 || unmatchedCount > 0;
};

export const hasUnmatchedReplaceItems = (data) =>
  (data?.unmatched_replace?.items?.length || 0) > 0;

export const hasSoldRefillContent = (data) => {
  if ((data?.sold_refill?.items?.length || 0) > 0) return true;
  if ((data?.sold_refill?.sold_items_count || 0) > 0) return true;
  return Boolean(getSoldRefillStatusMessage(data));
};

export const shouldShowAiSuggestionToast = (data) =>
  hasSoldRefillContent(data) || hasUnmatchedReplaceItems(data);

export const hasAiSuggestions = (data) => {
  if (!data) return false;
  if (hasAiSuggestionItems(data)) return true;
  return Boolean(
    getSoldRefillStatusMessage(data) || getUnmatchedReplaceStatusMessage(data)
  );
};

export const buildAiSuggestionMessage = (data, zoneLabel, t) => {
  if (!hasAiSuggestions(data)) {
    return null;
  }

  const soldCount = data?.sold_refill?.items?.length || 0;
  const unmatchedCount = data?.unmatched_replace?.items?.length || 0;

  if (soldCount > 0 || unmatchedCount > 0) {
    const zone = zoneLabel || t('this zone', { defaultValue: 'this zone' });

    if (soldCount > 0 && unmatchedCount > 0) {
      return t(
        'Replacement items for sold products and unmatched items have been identified in {{zone}}.',
        { zone }
      );
    }

    if (unmatchedCount > 0) {
      return t('Unmatched items were found in {{zone}}.', { zone });
    }

    return t(
      'Replacement items for sold products have been identified in {{zone}}.',
      { zone }
    );
  }

  const messages = [
    getSoldRefillStatusMessage(data),
    getUnmatchedReplaceStatusMessage(data),
  ].filter(Boolean);

  return messages.length ? messages.join(' ') : null;
};

const shortenUnmatchedReason = (reason) => {
  if (!reason) return '';
  const firstPart = reason.split(',')[0]?.trim();
  if (!firstPart) return reason;
  return firstPart.replace(/^different\s+/i, 'different ');
};

export const getSoldRefillLines = (data) =>
  (data?.sold_refill?.items || []).map((item) => ({
    soldSku: item?.sold_SKU || item?.SKU || '-',
    replacementSku:
      item?.recommend_SKU ||
      item?.replacement_SKU ||
      item?.replacement_sku ||
      item?.refill_SKU ||
      item?.suggest_SKU ||
      '-',
    soldPoProductId: item?.sold_po_product_id || null,
    recommendPoProductId: item?.recommend_po_product_id || null,
    matchSource: item?.match_source || null,
  }));

export const rowMatchesSoldProduct = (row, soldPoProductId) => {
  if (!row || !soldPoProductId) return false;
  return String(row.id ?? '') === String(soldPoProductId);
};

export const hasSoldRefillSuggestions = (data) =>
  (data?.sold_refill?.items?.length || 0) > 0;

export const getChatProductKey = (product) =>
  String(product?.stock_id || product?.id || product?.SKU || '');

export const findIssueRowForChatProduct = (product, issueRows = []) =>
  (issueRows || []).find(
    (row) =>
      (product?.stock_id &&
        String(row?.stock_id ?? '') === String(product.stock_id)) ||
      (product?.id && String(row?.id ?? '') === String(product.id))
  );

export const findIssueRowForStockSearchItem = (item, issueRows = []) =>
  (issueRows || []).find((row) => {
    const stockId = String(item?.stock_id ?? '');
    const productId =
      item?.id ?? item?.po_product_id ?? item?.product_id ?? null;
    return (
      (stockId && String(row?.stock_id ?? '') === stockId) ||
      (productId && String(row?.id ?? '') === String(productId))
    );
  });

export const isReturnedIssueRow = (row) => parseZoneQty(row?.return||row?.ReturnQty) > 0;

export const isReturnSearchEligibleIssueRow = (row) => {
  if (!row) return false;
  const issue = parseZoneQty(row?.issue);
  const sold = parseZoneQty(row?.sold);
  const returnQty = parseZoneQty(row?.return);
  const remainingBalance = issue - sold - returnQty;
  return issue > 0 && remainingBalance > 0;
};

export const isReissuableReturnedStockSearchItem = (item, issueRows = []) => {
  const row = findIssueRowForStockSearchItem(item, issueRows);
  return Boolean(row && isReturnedIssueRow(row));
};

export const isChatProductShowingAsAdded = (
  product,
  issueRows = [],
  addedProductKeys = null
) => {
  const key = getChatProductKey(product);
  if (addedProductKeys?.has?.(key)) {
    return true;
  }
  const row = findIssueRowForChatProduct(product, issueRows);
  if (!row) {
    return false;
  }
  return !isReturnedIssueRow(row);
};

export const reissueReturnedIssueRow = (row, apiItem = null) => {
  const issue = parseZoneQty(row?.issue);
  const sold = parseZoneQty(row?.sold);
  const returnQty = parseZoneQty(row?.return ?? row?.Return ?? row?.ReturnQty);
  const issueIncrement =
    apiItem != null
      ? parseZoneQty(apiItem?.issue ?? apiItem?.Issue)
      : 1;
  const newIssue = issue + issueIncrement;
  const newBalance = newIssue - sold - returnQty;

  const next = {
    ...row,
    issue: newIssue,
    return: returnQty,
    balance: newBalance,
    new_add: true,
  };

  if (!row?._changeSnapshot) {
    next._changeSnapshot = {
      issue,
      return: returnQty,
      balance: parseZoneQty(row?.balance),
    };
  }

  return next;
};

export const soldRefillItemsToChatProducts = (data) =>
  (data?.sold_refill?.items || []).map((item) => ({
    id: item?.recommend_po_product_id || null,
    SKU: item?.recommend_SKU || item?.SKU || '-',
    main_image: item?.recommend_main_image || '',
    stock_id: item?.recommend_stock_id || '',
    item_name: item?.recommend_item_name || '',
    collection_name: item?.recommend_collection_name || '',
    size_name: item?.recommend_size_name || '',
    refill_for_po_product_id: item?.sold_po_product_id || null,
    final_score: item?.final_score ?? null,
    match_source: item?.match_source || null,
  }));

export const REPLACE_CURRENT_ITEM_INTENT = 'replace_current_item';
export const REPLACE_UNMATCHED_INTENT = 'replace_unmatched';
export const ISSUE_SHORTAGE_INTENT = 'issue_shortage';
export const SUGGEST_SIMILAR_ITEMS_PRESET = 'suggest_similar_items';
export const SUGGEST_SIMILAR_ITEMS_INTENT = 'suggest_similar';

export const PRESET_COMPOSER_HINT_PRESETS = new Set([
  REPLACE_CURRENT_ITEM_INTENT,
  SUGGEST_SIMILAR_ITEMS_PRESET,
]);

export const isReplaceProductIntent = (intent) =>
  intent === REPLACE_UNMATCHED_INTENT || intent === REPLACE_CURRENT_ITEM_INTENT;

export const hasReplaceForPoProductId = (products = []) =>
  (products || []).some((product) => product?.replace_for_po_product_id);

export const resolveChatProductIntent = ({
  activePreset = null,
  messageText = '',
  data = null,
  products = [],
  unmatchedPoProductIds = [],
  isReplaceUnmatchedMessage = () => false,
}) => {
  if (activePreset === SUGGEST_SIMILAR_ITEMS_PRESET) {
    return SUGGEST_SIMILAR_ITEMS_INTENT;
  }

  if (
    activePreset === REPLACE_CURRENT_ITEM_INTENT ||
    hasReplaceForPoProductId(products)
  ) {
    return REPLACE_CURRENT_ITEM_INTENT;
  }

  if (
    isReplaceUnmatchedMessage(messageText) ||
    (data?.unmatched_po_product_ids?.length ?? 0) > 0
  ) {
    return REPLACE_UNMATCHED_INTENT;
  }

  return 'refill_sold';
};

export const resolveReplaceProductIntent = resolveChatProductIntent;

export const normalizeAiChatProducts = (products) =>
  (products || []).map((item) => ({
    id: item?.id || null,
    SKU: item?.SKU || '-',
    main_image: item?.main_image || '',
    stock_id: item?.stock_id || '',
    item_name: item?.item_name || '',
    collection_name: item?.collection_name || item?.recommend_collection_name || '',
    size_name: item?.size_name || item?.recommend_size_name || '',
    refill_for_po_product_id: item?.refill_for_po_product_id || null,
    replace_for_po_product_id: item?.replace_for_po_product_id || null,
    final_score: item?.final_score ?? null,
    match_source: item?.match_source || null,
  }));

export const buildRefillReplyFromSoldRefill = (data) => {
  const items = data?.sold_refill?.items || [];
  if (!items.length) return '';

  const lines = items.map((item) => {
    const soldSku = item?.sold_SKU || item?.SKU || '-';
    const recommendSku = item?.recommend_SKU || '-';
    const itemLabel = item?.recommend_item_name || '';
    return `${soldSku} → ${recommendSku}${itemLabel ? ` — ${itemLabel}` : ''}`;
  });

  return `Refill suggestions for ${items.length} of ${items.length} sold item(s):\n\n${lines.join('\n\n')}`;
};

export const getUnmatchedLines = (data) =>
  (data?.unmatched_replace?.items || []).map((item) => ({
    sku: item?.SKU || '-',
    reason: shortenUnmatchedReason(item?.reason) || item?.item_name || '-',
  }));

export const parseZoneQty = (value) => {
  if (value === null || value === undefined || value === '-') return 0;
  const num = parseInt(String(value), 10);
  return Number.isNaN(num) ? 0 : num;
};

export const getIssueRowBalance = (row) => {
  const balance = parseZoneQty(row?.balance ?? row?.Balance);
  if (balance > 0) {
    return balance;
  }

  const issue = parseZoneQty(row?.issue);
  const sold = parseZoneQty(row?.sold);
  const returnQty = parseZoneQty(row?.return ?? row?.Return ?? row?.ReturnQty);
  return Math.max(0, issue - sold - returnQty);
};

/** Row still has quantity in the zone (supports multi issue/return cycles). */
export const hasActiveIssueRowBalance = (row) => getIssueRowBalance(row) > 0;

export const applyNewReturnMarker = (row, previousReturn, previousBalance) => {
  if (row?.new_return && row?._changeSnapshot) {
    return row;
  }

  return {
    ...row,
    new_return: true,
    _changeSnapshot: {
      return: parseZoneQty(previousReturn),
      balance: previousBalance,
    },
  };
};

export const getIssueRowPoProductId = (row) => {
  const id =
    row?.po_product_id ??
    row?.product_id ??
    row?.id ??
    row?.Item_id ??
    null;
  if (id === null || id === undefined || id === '') return null;
  return String(id);
};

export const getUnmatchedItemRefs = (data) =>
  (data?.unmatched_replace?.items || []).map((item) => ({
    productId:
      item?.unmatchPoProductId ??
      item?.unmatch_po_product_id ??
      item?.po_product_id ??
      item?.product_id ??
      item?.id ??
      null,
    sku: String(item?.SKU ?? item?.sku ?? '').trim(),
    stockId: String(
      item?.stock_id ?? item?.stockId ?? item?.Stock_id ?? ''
    ).trim(),
  }));

export const rowMatchesUnmatchedItem = (row, ref) => {
  if (!row || !ref) return false;

  if (ref.productId != null && ref.productId !== '') {
    const targetId = String(ref.productId);
    const rowIds = [row?.id, row?.po_product_id, row?.product_id, row?.Item_id]
      .filter((value) => value != null && value !== '')
      .map((value) => String(value));

    if (rowIds.includes(targetId)) {
      return true;
    }
  }

  if (ref.sku) {
    const rowSku = String(row?.SKU ?? row?.sku ?? '').trim();
    if (rowSku && rowSku.toLowerCase() === ref.sku.toLowerCase()) {
      return true;
    }
  }

  if (ref.stockId) {
    const rowStockId = String(row?.stock_id ?? '').trim();
    if (rowStockId && rowStockId === ref.stockId) {
      return true;
    }
  }

  return false;
};

export const getUnmatchedProductIds = (data) =>
  getUnmatchedItemRefs(data)
    .map((ref) => ref.productId)
    .filter((value) => value != null && value !== '');

export const rowMatchesUnmatchedProduct = (row, unmatchPoProductId) => {
  if (!row || unmatchPoProductId == null || unmatchPoProductId === '') {
    return false;
  }

  if (typeof unmatchPoProductId === 'object') {
    return rowMatchesUnmatchedItem(row, unmatchPoProductId);
  }

  return rowMatchesUnmatchedItem(row, {
    productId: unmatchPoProductId,
    sku: '',
  });
};

export const getIssueUnmatchedPoProductIds = (data, issueRows = []) => {
  const refs = getUnmatchedItemRefs(data);
  if (!refs.length) return [];

  if (!issueRows?.length) {
    return getUnmatchedProductIds(data).map(String);
  }

  const matchedIds = [];

  refs.forEach((ref) => {
    const row = issueRows.find(
      (issueRow) =>
        rowMatchesUnmatchedItem(issueRow, ref) &&
        isActiveUnmatchedIssueRow(issueRow, data)
    );

    if (row) {
      const resolvedId = getIssueRowPoProductId(row);
      if (resolvedId) {
        matchedIds.push(resolvedId);
        return;
      }
    }

    if (ref.productId != null && ref.productId !== '') {
      matchedIds.push(String(ref.productId));
    }
  });

  return [...new Set(matchedIds)];
};

export const isRowUnmatchedForRemoval = (row, aiSuggestions) => {
  const refs = getUnmatchedItemRefs(aiSuggestions);
  return refs.some((ref) => rowMatchesUnmatchedItem(row, ref));
};

export const isActiveUnmatchedIssueRow = (row, aiSuggestions) =>
  isRowUnmatchedForRemoval(row, aiSuggestions) && hasActiveIssueRowBalance(row);

export const markRowsWithNewAdd = (rows, stockIds = []) => {
  const stockIdSet = new Set(
    (stockIds || []).map((stockId) => String(stockId)).filter(Boolean)
  );
  if (!stockIdSet.size) {
    return rows;
  }
  return rows.map((row) => {
    const stockId = String(row?.stock_id ?? '');
    if (stockId && stockIdSet.has(stockId)) {
      return { ...row, new_add: true };
    }
    return row;
  });
};

const findPreviousIssueRow = (row, previousRows = []) =>
  (previousRows || []).find((prev) => {
    const stockId = String(row?.stock_id ?? '');
    const rowId = String(row?.id ?? '');
    const prevStockId = String(prev?.stock_id ?? '');
    const prevId = String(prev?.id ?? '');
    return (
      (stockId && prevStockId && stockId === prevStockId) ||
      (rowId && prevId && rowId === prevId)
    );
  });

export const markRowsWithIncreasedReturn = (rows, previousRows = []) => {
  if (!previousRows?.length) {
    return rows;
  }
  return rows.map((row) => {
    const previousRow = findPreviousIssueRow(row, previousRows);
    if (!previousRow) {
      return row;
    }
    const previousReturn = parseZoneQty(previousRow?.return);
    const nextReturn = parseZoneQty(row?.return);
    if (nextReturn > previousReturn) {
      return applyNewReturnMarker(
        row,
        previousReturn,
        previousRow?.balance ?? row?.balance
      );
    }
    return row;
  });
};

const buildIncomingRowMatchKeys = (incomingRows = []) => {
  const keys = new Set();
  incomingRows.forEach((item) => {
    const stockId = String(item?.stock_id ?? '');
    const rowId = String(item?.id ?? '');
    if (stockId) {
      keys.add(`stock:${stockId}`);
    }
    if (rowId) {
      keys.add(`id:${rowId}`);
    }
  });
  return keys;
};

const isIncomingSearchMatch = (row, incomingMatchKeys) => {
  const stockId = String(row?.stock_id ?? '');
  const rowId = String(row?.id ?? '');
  return (
    (stockId && incomingMatchKeys.has(`stock:${stockId}`)) ||
    (rowId && incomingMatchKeys.has(`id:${rowId}`))
  );
};

export const applyReturnSearchUpdates = (
  mergedRows,
  previousRows = [],
  incomingRows = []
) => {
  if (!previousRows?.length || !incomingRows?.length) {
    return mergedRows;
  }

  const incomingMatchKeys = buildIncomingRowMatchKeys(incomingRows);

  return mergedRows.map((row) => {
    if (!isIncomingSearchMatch(row, incomingMatchKeys)) {
      return row;
    }

    const previousRow = findPreviousIssueRow(row, previousRows);
    if (!previousRow || !isReturnSearchEligibleIssueRow(previousRow)) {
      return row;
    }

    return applyReturnEqualToIssue({
      ...row,
      issue: parseZoneQty(row?.issue ?? previousRow?.issue),
      sold: parseZoneQty(row?.sold ?? previousRow?.sold),
      return: parseZoneQty(previousRow?.return),
    });
  });
};

export const markRowRefillSoldStatus = (rows, soldPoProductId) => {
  if (!soldPoProductId) {
    return rows;
  }
  return rows.map((row) =>
    String(row?.id ?? '') === String(soldPoProductId)
      ? { ...row, refillSoldStatus: true }
      : row
  );
};

export const applyReturnEqualToIssue = (item) => {
  const issue = parseZoneQty(item?.issue);
  const sold = parseZoneQty(item?.sold);
  const currentReturn = parseZoneQty(item?.return);
  const newReturn = issue - sold;

  if (newReturn <= currentReturn) {
    return item;
  }

  const next = {
    ...item,
    return: newReturn,
    balance: 0,
  };

  return applyNewReturnMarker(next, currentReturn, item?.balance);
};

export const incrementReturnOnUnmatchedRow = (rows, unmatchedPoProductId) => {
  if (!unmatchedPoProductId) return rows;
  return rows.map((row) => {
    if (!rowMatchesUnmatchedProduct(row, unmatchedPoProductId)) return row;
    return applyReturnEqualToIssue(row);
  });
};

export const enrichReplaceUnmatchedProducts = (
  products,
  unmatchedPoProductIds = []
) =>
  (products || []).map((product, index) => ({
    ...product,
    replace_for_po_product_id:
      product?.replace_for_po_product_id ||
      unmatchedPoProductIds[index] ||
      null,
  }));

export const enrichReplaceCurrentItemProducts = (products, data = null) => {
  const replaceTargetId =
    data?.matched_po_product_id || data?.unmatchPoProductId || null;
  if (!replaceTargetId) return products;

  return (products || []).map((product) => ({
    ...product,
    replace_for_po_product_id:
      product?.replace_for_po_product_id || replaceTargetId,
  }));
};

export const issueHasSoldItems = (rows) =>
  (rows || []).some((row) => parseZoneQty(row?.sold) > 0);

export const issueHasUnmatchedItems = (rows, data) =>
  (rows || []).some((row) => isActiveUnmatchedIssueRow(row, data));

export const EDIT_MODE_BASE_CHIPS = [
  {
    id: 'replace',
    labelKey: 'Replace current item',
    highlighted: false,
    showAiBadge: true,
  },
  {
    id: 'suggest',
    labelKey: 'Suggest similar items',
    highlighted: false,
    showAiBadge: true,
  },
];

export const getAiActionChips = (data, issueRows = []) => {
  const chips = [];
  const hasSoldSuggestions = (data?.sold_refill?.items?.length || 0) > 0;
  const hasUnmatchedSuggestions =
    (data?.unmatched_replace?.items?.length || 0) > 0;
  const showUnmatchedChips =
    hasUnmatchedSuggestions && issueHasUnmatchedItems(issueRows, data);
  const showSoldRefillChip =
    hasSoldSuggestions && issueHasSoldItems(issueRows);

  if (showUnmatchedChips) {
    chips.push(
      {
        id: 'replace_unmatched',
        labelKey: 'Replace unmatched items',
        highlighted: true,
        showAiBadge: true,
      },
      {
        id: 'remove_unmatched',
        labelKey: 'Remove unmatched items',
        highlighted: true,
        showAiBadge: true,
      }
    );
  }

  if (showSoldRefillChip) {
    chips.push({
      id: 'refill_sold',
      labelKey: 'Refill all sold items',
      highlighted: true,
      showAiBadge: true,
    });
  }

  return chips;
};
